import { RatioProgressBar, Stack } from '@cast/design-system';
import { bytesToUnit } from '@cast/utils';

import { DateRangeUnsupportedBanner } from '../_components/DateRangeUnsupportedBanner';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

import { CrossAzCommunicationCard } from './_components/CrossAzCommunicationCard';
import { useCrossAzCommunicationTrafficData } from './_hooks/useCrossAzCommunicationTrafficData';
import { isCrossAzDateRangeSupported } from './_utils/isCrossAzDateRangeSupported';

const TITLE = 'Cross-AZ communication traffic';
const TEST_ID = 'cross-az-communication-traffic-card';

const formatBytes = (bytes?: number | null): string => {
  if (bytes == null) {
    return '—';
  }
  if (bytes === 0) {
    return '0 Bytes';
  }
  const [value, unit] = bytesToUnit(bytes, 2);
  return `${value} ${unit}`;
};

export const CrossAzCommunicationTrafficCard = () => {
  const { timeZone, getRange } = useInventoryWorkloadsTimeRangeSelection();
  const { from, to } = getRange();
  const { topItems, datapoints, isLoading, isFetching, error, refetch } =
    useCrossAzCommunicationTrafficData();

  const isCardLoading = isLoading || (!!error && isFetching);

  if (!isCrossAzDateRangeSupported(from, to)) {
    return (
      <WidgetLayout title={TITLE} testId={TEST_ID}>
        <DateRangeUnsupportedBanner testId={`${TEST_ID}-unsupported-range`} />
      </WidgetLayout>
    );
  }

  const maxValue = Math.max(...topItems.map((item) => item.cost), 0);

  return (
    <CrossAzCommunicationCard
      title={TITLE}
      testId={TEST_ID}
      topItems={topItems.map((item) => ({
        id: item.id,
        name: item.name,
        color: item.color,
        value: item.cost,
      }))}
      datapoints={datapoints}
      timeZone={timeZone}
      unit=""
      formatValue={(value) => formatBytes(value)}
      formatTick={(value) => formatBytes(value)}
      formatTooltipValue={(value) => formatBytes(Number(value))}
      isLoading={isCardLoading}
      isFetching={isFetching}
      error={error}
      refetch={refetch}
      renderListEndContent={(item) => (
        <Stack alignItems="flex-end" gap={4} flex={1}>
          <span>{formatBytes(item.value)}</span>
          <RatioProgressBar
            size="sm"
            hideBorders
            sx={{ width: '100%' }}
            total={maxValue}
            bars={[
              {
                value: item.value,
                color: item.color,
              },
            ]}
          />
        </Stack>
      )}
    />
  );
};
